import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { NewsArticle, EventArticle } from '@/types/news.types'
import { fetchNewsArticles, fetchNewsForAdminContent, fetchEventArticles, addEventArticle, updateEventArticle, addNewsArticleWithFiles, updateNewsArticleWithFiles, deleteNewsArticle, deleteEventArticle } from './newsThunks'

interface NewsState {
  news: NewsArticle[]
  events: EventArticle[]
  loadingNews: boolean
  loadingEvents: boolean
  errorNews: string | null
  errorEvents: string | null

}

const initialState: NewsState = {
  news: [],
  events: [],
  loadingNews: false,
  loadingEvents: false,
  errorNews: null,
  errorEvents: null,
}

const newsSlice = createSlice({
  name: 'news',
  initialState,
  reducers: {
    addNewArticle:(state, action: PayloadAction<NewsArticle>)=>{
      state.news.push(action.payload)
    },
    addEventArticle:(state, action: PayloadAction<EventArticle>)=> {
      state.events.push(action.payload)
    },
    setNewsLoading:(state, action: PayloadAction<boolean>)=>{
      state.loadingNews = action.payload
    },
    setEventsLoading:(state, action: PayloadAction<boolean>)=>{
      state.loadingEvents = action.payload
    },
    setEventsError:(state, action: PayloadAction<string>)=>{
      state.errorEvents = action.payload
      state.loadingEvents= false
    },
    setNewsError:(state, action: PayloadAction<string>)=>{
      state.errorNews = action.payload
      state.loadingNews= false
    },
    clearEventsError: (state)=>{
      state.errorEvents= null
    },
    clearNewsError: (state)=>{
      state.errorNews= null
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchNewsArticles.pending, state => {
        state.loadingNews = true
        state.errorNews = null
      })
      .addCase(fetchNewsArticles.fulfilled, (state, action: PayloadAction<NewsArticle[]>) => {

        state.loadingNews = false
        state.news = action.payload
      })
      .addCase(fetchNewsArticles.rejected, (state, action: any) => {
        state.loadingNews = false
        state.errorNews = action.payload || action.error?.message || 'Failed to load news'
      })

      // Fetch News for Admin Content handlers
      .addCase(fetchNewsForAdminContent.pending, state => {
        state.loadingNews = true
        state.errorNews = null
      })
      .addCase(fetchNewsForAdminContent.fulfilled, (state, action: PayloadAction<NewsArticle[]>) => {
        state.loadingNews = false
        state.news = action.payload
      })
      .addCase(fetchNewsForAdminContent.rejected, (state, action: any) => {
        state.loadingNews = false
        state.errorNews = action.payload || action.error?.message || 'Failed to load news for admin'
      })

      .addCase(fetchEventArticles.pending, state => {
        state.loadingEvents = true
        state.errorEvents = null
      })
      .addCase(fetchEventArticles.fulfilled, (state, action: PayloadAction<EventArticle[]>) => {
        state.loadingEvents = false
        state.events = action.payload
      })
      .addCase(fetchEventArticles.rejected, (state, action: any) => {
        state.loadingEvents = false
        state.errorEvents = action.payload || action.error?.message || 'Failed to load events'
      })

      // Add News Article handlers (handled by addNewsArticleWithFiles)

      // Add News Article with Files handlers
      .addCase(addNewsArticleWithFiles.pending, state => {
        state.errorNews = null
      })
      .addCase(addNewsArticleWithFiles.fulfilled, (state, action: PayloadAction<NewsArticle>) => {
        state.news.push(action.payload)
      })
      .addCase(addNewsArticleWithFiles.rejected, (state, action: any) => {
        state.errorNews = action.payload || action.error?.message || 'Failed to add news article with files'
      })

      // Update News Article handlers (handled by updateNewsArticleWithFiles)

      // Update News Article with Files handlers
      .addCase(updateNewsArticleWithFiles.pending, state => {
        state.errorNews = null
      })
      .addCase(updateNewsArticleWithFiles.fulfilled, (state, action: PayloadAction<NewsArticle>) => {
        const index = state.news.findIndex(a => a.id === action.payload.id)
        if (index > -1) {
          state.news[index] = { ...state.news[index], ...action.payload }
        }
      })
      .addCase(updateNewsArticleWithFiles.rejected, (state, action: any) => {
        state.errorNews = action.payload || action.error?.message || 'Failed to update news article with files'
      })

      // Add Event Article handlers
      .addCase(addEventArticle.pending, state => {
        state.errorEvents = null
      })
      .addCase(addEventArticle.fulfilled, (state, action: PayloadAction<EventArticle>) => {
        state.events.push(action.payload)
      })
      .addCase(addEventArticle.rejected, (state, action: any) => {
        state.errorEvents = action.payload || action.error?.message || 'Failed to add event article'
      })

      // Update Event Article handlers
      .addCase(updateEventArticle.pending, state => {
        state.errorEvents = null
      })
      .addCase(updateEventArticle.fulfilled, (state, action: PayloadAction<any>) => {
        const index = state.events.findIndex(e => e.id === action.payload.id)
        if (index > -1) {
          state.events[index] = { ...state.events[index], ...action.payload }
        }
      })
      .addCase(updateEventArticle.rejected, (state, action: any) => {
        state.errorEvents = action.payload || action.error?.message || 'Failed to update event article'
      })

      // Delete News Article handlers
      .addCase(deleteNewsArticle.pending, state => {
        state.errorNews = null
      })
      .addCase(deleteNewsArticle.fulfilled, (state, action: PayloadAction<string>) => {
        state.news = state.news.filter(article => article.id !== action.payload)
      })
      .addCase(deleteNewsArticle.rejected, (state, action: any) => {
        state.errorNews = action.payload || action.error?.message || 'Failed to delete news article'
      })

      // Delete Event Article handlers
      .addCase(deleteEventArticle.pending, state => {
        state.errorEvents = null
      })
      .addCase(deleteEventArticle.fulfilled, (state, action: PayloadAction<string>) => {
        state.events = state.events.filter(event => event.id !== action.payload)
      })
      .addCase(deleteEventArticle.rejected, (state, action: any) => {
        state.errorEvents = action.payload || action.error?.message || 'Failed to delete event article'
      })
  },
})

export default newsSlice.reducer
